import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/UI/Modal';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { FiTrendingUp, FiTrendingDown, FiDownload, FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Title, Tooltip, Legend, Filler);

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const BASE_PATH = window.location.pathname.startsWith('/ecommerzaa') ? '/ecommerzaa' : '';

const EXPENSE_TYPES = [
  { value: 'shipping', label: 'Shipping' },
  { value: 'return_shipping', label: 'Return Shipping' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'commission', label: 'Commission' },
  { value: 'advertising', label: 'Advertising' },
  { value: 'salary', label: 'Salary' },
  { value: 'rent', label: 'Rent' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'software', label: 'Software Subscriptions' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'professional_fees', label: 'Professional Fees' },
  { value: 'bank_charges', label: 'Bank Charges' },
  { value: 'other', label: 'Other' },
];

const ProfitLoss = () => {
  const today = new Date();
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [from, setFrom] = useState(`${today.getFullYear()-2}-01-01`);
  const [to, setTo] = useState(today.toISOString().split('T')[0]);
  const [groupBy, setGroupBy] = useState('month');
  const [platform, setPlatform] = useState('');
  const [exportFormat, setExportFormat] = useState('csv');
  const [activeTab, setActiveTab] = useState('report');
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ expense_type: 'shipping', expense_category: '', amount: '', description: '', reference_type: '', reference_id: '', expense_date: today.toISOString().split('T')[0] });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      let url = `/reports/profit-loss.php?from=${from}&to=${to}&group_by=${groupBy}`;
      if (platform) url += `&platform=${platform}`;
      const res = await api.get(url);
      if (res.data.success) setData(res.data.data);
      else setError(res.data.message);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load P&L report');
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy, platform]);

  const fetchExpenses = useCallback(async () => {
    try {
      const res = await api.get(`/expenses/list.php?from=${from}&to=${to}`);
      if (res.data.success) setExpenses(res.data.data?.expenses || []);
    } catch (e) {}
  }, [from, to]);

  useEffect(() => { fetchReport(); fetchExpenses(); }, [fetchReport, fetchExpenses]);

  const handleExport = () => {
    if (exportFormat === 'pdf') { window.print(); return; }
    const ext = exportFormat === 'xls' ? 'xls' : exportFormat === 'doc' ? 'doc' : 'csv';
    let params = `from=${from}&to=${to}&group_by=${groupBy}&format=${exportFormat === 'csv' ? 'csv' : 'html'}` + (exportFormat !== 'csv' ? `&export_ext=${ext}` : '');
    if (platform) params += `&platform=${platform}`;
    window.open(`${BASE_PATH}/backend/public/api/reports/profit-loss.php?${params}`, '_blank');
  };

  const openCreateExpense = () => {
    setEditingExpense(null);
    setExpenseForm({ expense_type: 'shipping', expense_category: '', amount: '', description: '', reference_type: '', reference_id: '', expense_date: today.toISOString().split('T')[0] });
    setShowExpenseForm(true);
  };

  const openEditExpense = (e) => {
    setEditingExpense(e);
    setExpenseForm({
      expense_type: e.expense_type || 'shipping',
      expense_category: e.expense_category || '',
      amount: e.amount || '',
      description: e.description || '',
      reference_type: e.reference_type || '',
      reference_id: e.reference_id || '',
      expense_date: (e.expense_date || '').split(' ')[0] || today.toISOString().split('T')[0],
    });
    setShowExpenseForm(true);
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...expenseForm, amount: parseFloat(expenseForm.amount || 0) };
      if (editingExpense) {
        await api.post('/expenses/update.php', { id: editingExpense.id, ...payload });
      } else {
        await api.post('/expenses/create.php', payload);
      }
      setShowExpenseForm(false);
      fetchExpenses();
      fetchReport();
    } catch (e) { setError('Failed to save expense'); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense record?')) return;
    try { await api.post('/expenses/delete.php', { id }); fetchExpenses(); fetchReport(); }
    catch (e) { setError('Failed to delete expense'); }
  };

  if (loading && !data) return <div className="text-center py-20 text-gray-600 dark:text-gray-400">Loading P&L report...</div>;

  const s = data?.summary || {};
  const details = data?.details || [];
  const expenseBreakdown = s?.expense_breakdown || [];

  // Chart data - Monthly trend
  const monthlyLabels = details.filter(d => d.period).map(d => { const [y, m] = d.period.split('-'); return `${MONTHS[parseInt(m)-1]} ${y}`; });
  const monthlyChartData = {
    labels: monthlyLabels.length ? monthlyLabels : ['No Data'],
    datasets: [
      { label: 'Revenue', data: details.map(d => d.revenue || 0), backgroundColor: 'rgba(59, 130, 246, 0.7)', borderColor: '#3b82f6', borderWidth: 1 },
      { label: 'Gross Profit', data: details.map(d => d.gross_profit || 0), backgroundColor: 'rgba(16, 185, 129, 0.7)', borderColor: '#10b981', borderWidth: 1 },
      { label: 'Net Profit', data: details.map(d => d.net_profit || 0), backgroundColor: 'rgba(139, 92, 246, 0.7)', borderColor: '#8b5cf6', borderWidth: 1 },
    ],
  };

  // Expense breakdown chart
  const expLabels = expenseBreakdown.map(e => { const t = EXPENSE_TYPES.find(x => x.value === e.expense_type); return t ? t.label : e.expense_type; });
  const expColors = ['#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6','#8b5cf6','#ec4899','#14b8a6','#f43f5e','#6366f1','#84cc16','#a855f7'];
  const expenseChartData = {
    labels: expLabels.length ? expLabels : ['No Expenses'],
    datasets: [{ data: expenseBreakdown.map(e => parseFloat(e.total) || 0), backgroundColor: expColors.slice(0, expenseBreakdown.length), borderWidth: 1 }],
  };

  const rcChartData = {
    labels: ['Net Revenue', 'COGS', 'Marketplace Fees', 'Shipping Costs', 'Refunds', 'Other Expenses'],
    datasets: [{
      data: [Math.max(0, (s.net_profit || 0)), s.total_cogs || 0, s.marketplace_fees || 0, s.shipping_costs || 0, s.refunds || 0, s.other_expenses || 0],
      backgroundColor: ['#10b981','#ef4444','#f97316','#eab308','#ec4899','#8b5cf6'],
      borderWidth: 1,
    }],
  };

  const pluralize = (n, s) => `${n} ${n === 1 ? s : s + 's'}`;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Profit & Loss</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Revenue, costs, expenses, and net profit analysis</p>
        </div>
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg">{error}</div>}

      {/* Tabs */}
      <div className="flex gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-md p-1 w-fit">
        <button onClick={() => setActiveTab('report')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'report' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>P&L Report</button>
        <button onClick={() => setActiveTab('expenses')} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'expenses' ? 'bg-primary-600 text-white' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>Expenses</button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Group By</label>
            <select value={groupBy} onChange={e => setGroupBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
              <option value="month">Month</option>
              <option value="category">Category</option>
              <option value="platform">Platform</option>
              <option value="detail">Detail (Orders)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Platform</label>
            <select value={platform} onChange={e => setPlatform(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
              <option value="">All Platforms</option>
              {(data?.platforms || []).map(p => <option key={p.marketplace_type} value={p.marketplace_type}>{p.marketplace_type.charAt(0).toUpperCase() + p.marketplace_type.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Export As</label>
            <div className="flex gap-1">
              <select value={exportFormat} onChange={e => setExportFormat(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white">
                <option value="csv">CSV</option>
                <option value="xls">Excel</option>
                <option value="doc">DOC</option>
                <option value="pdf">PDF (Print)</option>
              </select>
              <button onClick={handleExport}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm transition-colors whitespace-nowrap flex items-center gap-1"><FiDownload size={14} /> Export</button>
            </div>
          </div>
          <button onClick={fetchReport}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors">Apply</button>
        </div>
      </div>

      {activeTab === 'report' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Orders</p>
              <p className="text-xl font-bold text-gray-800 dark:text-white">{s.order_count || 0}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Revenue (Net)</p>
              <p className="text-xl font-bold text-blue-600">₹{(s.total_revenue || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">COGS</p>
              <p className="text-xl font-bold text-red-600">₹{(s.total_cogs || 0).toFixed(2)}</p>
            </div>
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 ${(s.gross_profit || 0) >= 0 ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'}`}>
              <p className="text-xs text-gray-500 dark:text-gray-400">Gross Profit</p>
              <p className={`text-xl font-bold ${(s.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>₹{(s.gross_profit || 0).toFixed(2)}</p>
              <p className="text-xs text-gray-400">{(s.gross_margin || 0).toFixed(1)}% margin</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Mktplace Fees</p>
              <p className="text-xl font-bold text-orange-600">₹{(s.marketplace_fees || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Shipping Costs</p>
              <p className="text-xl font-bold text-yellow-600">₹{(s.shipping_costs || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Refunds</p>
              <p className="text-xl font-bold text-pink-600">₹{(s.refunds || 0).toFixed(2)}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">Other Expenses</p>
              <p className="text-xl font-bold text-purple-600">₹{(s.other_expenses || 0).toFixed(2)}</p>
            </div>
            <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 col-span-2 md:col-span-2 xl:col-span-2 ${(s.net_profit || 0) >= 0 ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'}`}>
              <p className="text-xs text-gray-500 dark:text-gray-400">Net Profit / Loss</p>
              <div className="flex items-baseline gap-3">
                <p className={`text-2xl font-bold ${(s.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ₹{(s.net_profit || 0).toFixed(2)}
                </p>
                <span className={`text-sm font-medium ${(s.profit_margin || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {(s.profit_margin || 0) >= 0 ? <FiTrendingUp className="inline mr-1" /> : <FiTrendingDown className="inline mr-1" />}
                  {Math.abs(s.profit_margin || 0).toFixed(1)}% margin
                </span>
              </div>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Monthly Bar Chart */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 xl:col-span-2">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                {groupBy === 'month' ? 'Monthly P&L Trend' : groupBy === 'category' ? 'Category-wise P&L' : groupBy === 'platform' ? 'Platform-wise P&L' : 'Per-Order P&L'}
              </h3>
              <div className="h-72">
                <Bar data={monthlyChartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { position: 'top', labels: { boxWidth: 12, padding: 12, font: { size: 11 } } } },
                  scales: { y: { beginAtZero: true, ticks: { callback: v => '₹' + v.toLocaleString() } } },
                }} />
              </div>
            </div>

            {/* Expense Breakdown Pie */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Expense Breakdown</h3>
              <div className="h-64">
                <Doughnut data={expenseChartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right', labels: { boxWidth: 12, padding: 8, font: { size: 10 } } },
                    tooltip: { callbacks: { label: ctx => { const total = ctx.dataset.data.reduce((a, b) => a + b, 0); return ` ${ctx.label}: ₹${ctx.parsed.toFixed(2)} (${total > 0 ? ((ctx.parsed/total)*100).toFixed(1) : 0}%)`; } } },
                  },
                }} />
              </div>
            </div>
          </div>

          {/* Revenue vs Costs Doughnut */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Revenue Distribution</h3>
              <div className="h-64">
                <Doughnut data={rcChartData} options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { position: 'right', labels: { boxWidth: 12, padding: 8, font: { size: 10 } } },
                    tooltip: { callbacks: { label: ctx => { const total = ctx.dataset.data.reduce((a, b) => a + b, 0); return ` ${ctx.label}: ₹${ctx.parsed.toFixed(2)} (${total > 0 ? ((ctx.parsed/total)*100).toFixed(1) : 0}%)`; } } },
                  },
                }} />
              </div>
            </div>

            {/* Detail Table */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden xl:col-span-2">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                  {groupBy === 'month' ? 'Monthly Breakdown' : groupBy === 'category' ? 'Category-wise Breakdown' : groupBy === 'platform' ? 'Platform-wise Breakdown' : 'Order-wise Details'}
                </h3>
                <span className="text-sm text-gray-500">{details.length} rows</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                    <tr>
                      {groupBy === 'month' && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>}
                      {groupBy === 'category' && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>}
                      {groupBy === 'platform' && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>}
                      {groupBy === 'detail' && <><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th><th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th></>}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">COGS</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Profit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Fees</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Shipping</th>
                      {groupBy === 'detail' && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Refunds</th>}
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {details.length === 0 ? (
                      <tr><td colSpan={10} className="px-6 py-12 text-center text-gray-500">No data found for selected period.</td></tr>
                    ) : details.map((row, i) => {
                      const label = groupBy === 'month' ? (() => { const [y, m] = row.period.split('-'); return `${MONTHS[parseInt(m)-1]} ${y}`; })()
                        : groupBy === 'category' ? row.category_name
                        : groupBy === 'platform' ? (row.platform_name || '').charAt(0).toUpperCase() + (row.platform_name || '').slice(1)
                        : row.order_no;
                      return (
                        <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          {groupBy === 'month' && <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{label}</td>}
                          {groupBy === 'category' && <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{label}</td>}
                          {groupBy === 'platform' && <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white capitalize">{label}</td>}
                          {groupBy === 'detail' && <><td className="px-4 py-3 text-sm font-medium text-blue-600">{label}</td><td className="px-4 py-3 text-sm text-gray-900">{row.customer_name}</td><td className="px-4 py-3 text-sm text-gray-500">{row.order_date ? new Date(row.order_date).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'}) : '-'}</td><td className="px-4 py-3 text-sm capitalize text-gray-600">{row.platform_name || 'Direct'}</td></>}
                          <td className="px-4 py-3 text-sm text-right text-gray-600">{row.order_count || 1}</td>
                          <td className="px-4 py-3 text-sm text-right text-blue-600 font-medium">₹{(row.revenue || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-right text-red-600">₹{(row.cogs || 0).toFixed(2)}</td>
                          <td className={`px-4 py-3 text-sm text-right font-medium ${(row.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>₹{(row.gross_profit || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-right text-orange-600">₹{(row.marketplace_fees || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 text-sm text-right text-yellow-600">₹{(row.shipping_costs || 0).toFixed(2)}</td>
                          {groupBy === 'detail' && <td className="px-4 py-3 text-sm text-right text-pink-600">₹{(row.refunds || 0).toFixed(2)}</td>}
                          <td className={`px-4 py-3 text-sm text-right font-bold ${(row.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>₹{(row.net_profit || 0).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {details.length > 0 && (
                    <tfoot className="bg-gray-50 dark:bg-gray-700 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                      <tr>
                        {groupBy === 'month' && <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                        {groupBy === 'category' && <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                        {groupBy === 'platform' && <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total</td>}
                        {groupBy === 'detail' && <td colSpan="4" className="px-4 py-3 text-sm text-gray-800 dark:text-white">Total ({details.length} orders)</td>}
                        <td className="px-4 py-3 text-sm text-right text-gray-800 dark:text-white">{details.reduce((a, r) => a + (r.order_count || 1), 0)}</td>
                        <td className="px-4 py-3 text-sm text-right text-blue-600">₹{details.reduce((a, r) => a + (r.revenue || 0), 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-red-600">₹{details.reduce((a, r) => a + (r.cogs || 0), 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-green-600">₹{details.reduce((a, r) => a + (r.gross_profit || 0), 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-orange-600">₹{details.reduce((a, r) => a + (r.marketplace_fees || 0), 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-right text-yellow-600">₹{details.reduce((a, r) => a + (r.shipping_costs || 0), 0).toFixed(2)}</td>
                        {groupBy === 'detail' && <td className="px-4 py-3 text-sm text-right text-pink-600">₹{details.reduce((a, r) => a + (r.refunds || 0), 0).toFixed(2)}</td>}
                        <td className={`px-4 py-3 text-sm text-right font-bold ${details.reduce((a, r) => a + (r.net_profit || 0), 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          ₹{details.reduce((a, r) => a + (r.net_profit || 0), 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 text-sm text-gray-500 dark:text-gray-400">
            <p><strong>How P&L is calculated:</strong> <strong>Revenue</strong> = Net amount from delivered orders. <strong>COGS</strong> = Product cost price &times; quantity sold. <strong>Gross Profit</strong> = Revenue - COGS. <strong>Net Profit</strong> = Gross Profit - Marketplace Fees - Shipping Costs - Refunds - Other Expenses.</p>
          </div>
        </>
      )}

      {activeTab === 'expenses' && (
        <>
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">{pluralize(expenses.length, 'expense')} recorded</p>
            <button onClick={openCreateExpense} className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm transition-colors flex items-center gap-1"><FiPlus size={14} /> Add Expense</button>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {expenses.length === 0 ? (
                    <tr><td colSpan="5" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">No expenses found. Click "Add Expense" to record shipping costs, return charges, and other expenses.</td></tr>
                  ) : expenses.map(ex => {
                    const t = EXPENSE_TYPES.find(x => x.value === ex.expense_type);
                    return (
                      <tr key={ex.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">{new Date(ex.expense_date).toLocaleDateString('en-IN', {day:'2-digit',month:'short',year:'numeric'})}</td>
                        <td className="px-6 py-4"><span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">{t ? t.label : ex.expense_type}</span></td>
                        <td className="px-6 py-4 text-sm text-gray-800 dark:text-white max-w-xs truncate">{ex.description || '-'}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-right text-red-600">₹{parseFloat(ex.amount || 0).toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-center space-x-2">
                          <button onClick={() => openEditExpense(ex)} className="text-primary-600 hover:text-primary-900"><FiEdit2 size={15} /></button>
                          <button onClick={() => handleDeleteExpense(ex.id)} className="text-red-600 hover:text-red-900"><FiTrash2 size={15} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Expense Form Modal */}
      <Modal isOpen={showExpenseForm} onClose={() => setShowExpenseForm(false)} title={editingExpense ? 'Edit Expense' : 'Add Expense'}>
        <form onSubmit={handleExpenseSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Expense Type *</label>
              <select value={expenseForm.expense_type} onChange={e => setExpenseForm({...expenseForm, expense_type: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required>
                {EXPENSE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Amount (₹) *</label>
              <input type="number" step="0.01" min="0" value={expenseForm.amount} onChange={e => setExpenseForm({...expenseForm, amount: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Date *</label>
              <input type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <input type="text" value={expenseForm.expense_category} onChange={e => setExpenseForm({...expenseForm, expense_category: e.target.value})} placeholder="e.g. Amazon shipping"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea value={expenseForm.description} onChange={e => setExpenseForm({...expenseForm, description: e.target.value})} rows="2" placeholder="Describe the expense..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reference Type</label>
              <select value={expenseForm.reference_type} onChange={e => setExpenseForm({...expenseForm, reference_type: e.target.value, reference_id: ''})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500">
                <option value="">None (General Expense)</option>
                <option value="order">Order</option>
                <option value="return">Return</option>
                <option value="purchase">Purchase</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reference ID</label>
              <input type="number" min="0" value={expenseForm.reference_id} onChange={e => setExpenseForm({...expenseForm, reference_id: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500" placeholder="ID number" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={() => setShowExpenseForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            <button type="submit" className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">{editingExpense ? 'Update' : 'Add'} Expense</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProfitLoss;
